import { ReactNode, useEffect, useRef, useState } from 'react';
import { useChatStore } from '../store/chat-store';

interface Props {
  children: ReactNode;
}

/**
 * Floating-bubble launcher pattern (Intercom / Tawk style).
 *
 * Renders a fixed-position FAB at bottom-right of the host viewport. Clicking
 * it expands a chat panel anchored to the same corner. The actual chat sits
 * inside the panel and stays mounted across open/close so connections and
 * message history persist.
 *
 * Lives inside the widget's shadow DOM, so the host page's CSS can't interfere
 * with the launcher's layout.
 */
export function ChatLauncher({ children }: Props) {
  const [open, setOpen] = useState(false);
  // Track unread messages while the panel is closed. We count any new
  // message that arrives while open=false.
  const [unread, setUnread] = useState(0);
  const lastSeenCount = useRef(0);
  const messageCount = useChatStore(s => s.messages.length);

  // When a message arrives while closed, bump the unread badge.
  useEffect(() => {
    if (open) {
      lastSeenCount.current = messageCount;
      setUnread(0);
      return;
    }
    const delta = messageCount - lastSeenCount.current;
    if (delta > 0) setUnread(delta);
  }, [messageCount, open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className="vip-launcher-host">
      {/* The panel keeps the children mounted always — we only toggle visibility
          so that the chat keeps its WebSocket open across open/close. */}
      <div
        role="dialog"
        aria-label="VIP Chat"
        aria-hidden={!open}
        className={`vip-launcher-panel ${open ? 'is-open' : 'is-closed'}`}
      >
        {/* No separate close button: the FAB chevron toggles the panel and
            Esc closes it. The chat header's own controls stay unobstructed.
            The chat also renders the branded "Powered by" band, so the
            launcher doesn't add its own (avoids a duplicate footer). */}
        <div className="vip-launcher-body">{children}</div>
      </div>

      <button
        type="button"
        aria-label={open ? 'Close chat' : 'Open chat'}
        aria-expanded={open}
        className={`vip-launcher-fab ${open ? 'is-open' : ''}`}
        onClick={() => setOpen(v => !v)}
      >
        {open ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        )}
        {!open && unread > 0 && (
          <span className="vip-launcher-badge" aria-label={`${unread} unread messages`}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
    </div>
  );
}
